import React, { useContext, useEffect, useState } from "react"

import { SiteProps } from '../../interfaces'
import { validElectric } from '../../types'
import { BookerContext } from '../../contexts/BookerContext'
import { formatMoneyFromCents } from '../../utils/formatMoney'
import Button from '../ui/Button'
import classNames from '../../utils/classNames'
import Features from '../DetailsPayment/Features'
import BookerButton from '../ui/BookerButton'
import { CheckCircleIcon, ChevronRightIcon } from '@heroicons/react/24/solid'
import { getTextColorBasedOnBackground } from './Prerequisites'
import formatLengthUnit from '~/utils/formatLengthUnit'
import { features } from "./SiteFeatures"



interface ComponentProps {
  nights: number
  site: SiteProps
  isAvailable: boolean
  setActiveSite: any
  userElectric: validElectric
  setSelectedSite: (v: SiteProps) => void
  selectedSite: SiteProps
  openedSite: SiteProps
  setOpenedSite: (v: SiteProps) => void
  setSitePhotos: React.Dispatch<React.SetStateAction<Record<string, string[]>>>
  sitePhotos: Record<string, string[]>
  dataTestId: string
  index: number
}


const Site = (props: ComponentProps) => {
  const { endDate, startDate, campingStyle, secondaryColor, camp } = useContext(BookerContext)
  const isSiteOpened = props?.openedSite?.id === props.site.id
  const isSiteSelected = props?.selectedSite?.id === props.site.id
  const [isFeaturesCuttedOff, setIsFeaturesCuttedOff] = useState(false)

  const checkSite = () => {
    if (isSiteSelected) {
      props.setSelectedSite(null)
    } else {
      props.setSelectedSite(props.site)
    }
  }

  const filteredFeatures = features.filter(
    (feature) =>
      props.openedSite?.[feature] ||
      ["has_wifi", "electric", "water", "has_linens", "fire_pit", "picnic_table", "has_towels"].includes(feature),
  )

  useEffect(() => {
    if (isSiteOpened && window.screen.width < 1024) {
      setIsFeaturesCuttedOff(true)
    } else {
      setIsFeaturesCuttedOff(false)
    }
  }, [props.openedSite])

  return (
    <>
      <Button
        disabled={!props.isAvailable}
        id="button-site"
        variant="white"
        onClick={(e) => {
          if (!isSiteOpened) {
            props.setOpenedSite(props.site)
            props.setSelectedSite(props.site)
          } else {
            props.setOpenedSite(null)
            props.setSelectedSite(null)
          }
        }}
        className={classNames("w-full rounded-lg transition-all border-gray-100")}
        style={{
          boxShadow: "0px 1px 6px 0px #00000014",
          background: isSiteSelected && !isSiteOpened && secondaryColor,
          border: isSiteSelected && isSiteOpened && `1px solid ${secondaryColor}`,
          color: isSiteSelected && !isSiteOpened && getTextColorBasedOnBackground(secondaryColor),
          padding: "6px 14px 6px 14px",
        }}
        dataTestId={props.dataTestId}
      >
        <div className="">
          <div className="relative flex justify-between flex-wrap gap-y-1.5">
            <div className="flex items-center">
              <div className="flex items-center gap-2.5 flex-col-reverse xs:flex-row">
                <div className="flex gap-2.5 items-center">
                  {props?.selectedSite?.id === props.site.id ? (
                    <CheckCircleIcon
                      onClick={(e) => {
                        e.stopPropagation()
                        checkSite()
                      }}
                      className="w-8 h-8"
                      style={{ color: isSiteOpened && secondaryColor }}
                    />
                  ) : (
                    <div
                      onClick={(e) => {
                        e.stopPropagation()
                        checkSite()
                      }}
                      className="rounded-full border border-gray-2 w-8 h-8"
                      style={{ width: 22.75, height: 22.75, marginLeft: 2.625, marginRight: 2.625 }}
                    />
                  )}
                  <div className="flex flex-col justify-start whitespace-nowrap">
                    <div className="font-semibold text-left">{props.site.name}</div>
                    <div className="flex items-center gap-x-1 text-sm" style={{ color: !isSiteSelected && "#888" }}>
                      {[
                        props.site.electric &&
                          props.site.electric !== 'none' &&
                          (props.site.electric === 'basic' ? 'Electric' : `${props.site.electric}A`),
                        props.site.max_rig_length && `${props.site.max_rig_length}${formatLengthUnit(camp.length_unit)}`,
                        props.site.water && 'W',
                        props.site.sewage && 'S',
                        props.site.minimum_capacity && `${props.site.minimum_capacity} min`,
                        props.site.camper_capacity && `${props.site.camper_capacity} max`,
                      ]
                        .filter(Boolean)
                        .map((item, index, array) => (
                          <React.Fragment key={item}>
                            {item}
                            {index < array.length - 1 && <div className="w-0.75 h-0.75 bg-gray-400 rounded-full" />}
                          </React.Fragment>
                        ))}
                    </div>
                  </div>
                </div>
                {props.site.badge_label && (
                  <span className="inline-flex items-center px-3 py-0.5 ml-1.5 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    {props.site.badge_label}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {props.site.calculated_nightly_price_in_cents != 0 && (
                <span className="ml-1 opacity-75">
                  {formatMoneyFromCents(props.site.calculated_nightly_price_in_cents, camp.currency)}{" "}
                  {props.site.calculated_frequency_label}
                </span>
              )}
              <div
                onClick={(e) => {
                  e.stopPropagation()
                  if (!isSiteOpened) {
                    props.setOpenedSite(props.site)
                  } else {
                    props.setOpenedSite(null)
                  }
                }}
                className={classNames(
                  "bg-gray-200 p-3 rounded relative hover:text-black",
                  !isSiteOpened && isSiteSelected && "bg-opacity-30 hover:bg-opacity-100",
                  isSiteOpened && isSiteSelected && "hover:bg-gray-300",
                )}
                style={{ transition: "all .3s" }}
              >
                <ChevronRightIcon className={classNames("h-3 transform", isSiteOpened && "rotate-90")} />
              </div>
            </div>
          </div>
          {isSiteOpened && (
            <>
              <Features
                visible={true}
                nights={endDate?.diff(startDate, "days")}
                confirmCallback={() => {}}
                userElectric={props.userElectric}
                userCampingStyle={campingStyle}
                selectedSite={props.openedSite}
                isFeaturesCuttedOff={isFeaturesCuttedOff}
                sitePhotos={props.sitePhotos}
                setSitePhotos={props.setSitePhotos}
                features={filteredFeatures}
              />
              <div className="flex flex-col xs:flex-row gap-3 items-stretch">
                {filteredFeatures?.length > 6 && (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsFeaturesCuttedOff((prev) => !prev)
                    }}
                    children={`${isFeaturesCuttedOff ? "See all" : "Hide"} features`}
                    variant="none"
                    className="px-8 block lg:hidden whitespace-nowrap h-full w-full xs:w-1/2 font-semibold underline"
                    style={{ height: 45 }}
                  />
                )}

                {isSiteSelected ? (
                  <Button
                    className={"relative w-full bg-transparent font-bold"}
                    onClick={(e) => {
                      e.stopPropagation()
                      checkSite()
                    }}
                    style={{
                      border: `2px solid ${secondaryColor}`,
                      color: secondaryColor,
                    }}
                    variant="none"
                    hoverBg={secondaryColor}
                    hoverText={getTextColorBasedOnBackground(secondaryColor)}
                    dataTestId={`camps-site-selector-button-deselect-site-${props.index}`}
                  >
                    Deselect {window.screen.width > 425 ? props.site.name : ""}
                  </Button>
                ) : (
                  <BookerButton
                    minWidth={"100%"}
                    className={"relative w-full bg-white"}
                    buttonClassNames={"truncate lg:overflow-visible"}
                    secondaryColor={window.screen.width < 1024 ? "#fff" : secondaryColor}
                    buttonText={`Select ${window.screen.width > 425 ? props.site.name : ""}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      checkSite()
                    }}
                    isInternalComponentHidden={window.screen.width < 1024}
                    style={{
                      border: window.screen.width < 1024 && `2px solid ${secondaryColor}`,
                      color: window.screen.width < 1024 && secondaryColor,
                    }}
                    componentWidth={window.screen.width > 425 && window.screen.width < 1024 ? "50%" : "100%"}
                    dataTestId={`camps-site-selector-button-select-site-${props.index}`}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </Button>
    </>
  )
}

export const SitePlaceholder = () => {
  return (
    <>
      <Button
        id="button-site"
        variant="white"
        className={classNames("w-full rounded-lg transition-all border-gray-200 animate-pulsing")}
        style={{
          boxShadow: "0px 1px 6px 0px #00000014",
        }}
      >
        <div className="">
          <div className="relative flex justify-between flex-wrap gap-y-1.5">
            <div className="flex items-center">
              <div className="flex items-center gap-2.5 flex-col-reverse xs:flex-row">
                <div className="flex gap-2.5 items-center">
                  <div className="w-8 h-8 bg-gray-200 animate-pulsing"></div>
                  <div className="flex flex-col justify-start whitespace-nowrap gap-1">
                    <div className="font-semibold text-left h-2.5 w-24 rounded-xl bg-gray-200 animate-pulsing"></div>
                    <div className="font-semibold text-left h-2.5 w-24 rounded-xl bg-gray-200 animate-pulsing"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-2.5 w-12 rounded-xl bg-gray-200 animate-pulsing"></div>
              <div className={classNames("w-8 h-8 bg-gray-200 animate-pulsing")}></div>
            </div>
          </div>
        </div>
      </Button>
    </>
  )
}

export default Site
